import {
  Box,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import { useState, type MouseEvent, type ReactNode } from 'react';
import { hideBelowSx } from '../collapse';
import { createActivationKeyHandler } from '../rowInteraction';
import {
  TITLE_FLEX,
  VALUE_WIDTH,
  type LedgerCells,
  type LedgerCollapse,
  type LedgerColumn,
  type LedgerRowMenu,
} from '../types';
import { LedgerRowText } from './LedgerRowText';

interface BaseLedgerRowProps {
  columns: readonly LedgerColumn[];
  collapse: LedgerCollapse;
  /** Cell content by column key; keys with no column are ignored. */
  cells?: LedgerCells;
  lead?: ReactNode;
  title: ReactNode;
  titleTag?: ReactNode;
  /** Everything the hidden columns carried, for widths below the collapse point. */
  meta?: string;
  value?: ReactNode;
  showValues?: boolean;
  /**
   * The cell content is still loading: columns render placeholders, and so
   * does the Value box when no value has arrived either. The title, lead and
   * any value already known render as normal.
   */
  pending?: boolean;
  /** Cancelled rows dim; nothing else does. */
  dimmed?: boolean;
  hoverCard?: ReactNode;
  /** Omit for no context menu at all — an empty menu is worse than none. */
  menu?: LedgerRowMenu;
  onClick?: () => void;
  sx?: SxProps<Theme>;
  'data-testid'?: string;
  /** Machine-readable status attribute on the row root (e2e contract). */
  'data-status'?: string;
  'aria-label'?: string;
}

interface MenuPosition {
  top: number;
  left: number;
}

/**
 * Internal layout shell for ledger rows. Not exported from the ledger index —
 * use one of the row variants.
 *
 * Knows about slots, columns, the collapse point, the hover slot and the menu
 * slot. It knows nothing about statuses, references, work types or money.
 */
export const BaseLedgerRow = ({
  columns,
  collapse,
  cells = {},
  lead,
  title,
  titleTag,
  meta,
  value,
  showValues = true,
  pending = false,
  dimmed = false,
  hoverCard,
  menu,
  onClick,
  sx = [],
  'data-testid': dataTestId,
  'data-status': dataStatus,
  'aria-label': ariaLabel,
}: BaseLedgerRowProps) => {
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);

  const interactionProps = onClick
    ? {
        onClick,
        onKeyDown: createActivationKeyHandler(onClick),
        role: 'button' as const,
        tabIndex: 0,
      }
    : undefined;

  const openMenu = (event: MouseEvent) => {
    event.preventDefault();
    setMenuPosition({ top: event.clientY, left: event.clientX });
  };

  return (
    <>
      <Box
        {...interactionProps}
        onContextMenu={menu ? openMenu : undefined}
        aria-busy={pending || undefined}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        data-status={dataStatus}
        sx={[
          {
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 0.75,
            minHeight: 36,
            opacity: dimmed ? 0.55 : 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'action.hover' },
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: -2,
            },
          },
          interactionProps !== undefined && { cursor: 'pointer' },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <Box sx={{ flex: `${TITLE_FLEX} 1 0`, minWidth: 0 }}>
          <LedgerRowText
            lead={lead}
            title={title}
            titleTag={titleTag}
            meta={meta}
            collapse={collapse}
            hoverCard={hoverCard}
          />
        </Box>

        {columns.map(column => (
          <Box
            key={column.key}
            sx={{
              flex: `${column.flex} 1 0`,
              minWidth: 0,
              ...hideBelowSx(collapse),
            }}
          >
            {pending ? (
              <Skeleton variant="text" width="70%" />
            ) : (
              <Typography
                component="div"
                variant="caption"
                color="text.secondary"
                noWrap
              >
                {cells[column.key] ?? ''}
              </Typography>
            )}
          </Box>
        ))}

        {showValues && (
          <Box sx={{ width: VALUE_WIDTH, flexShrink: 0, textAlign: 'right' }}>
            {pending && value == null ? (
              <Skeleton variant="text" sx={{ ml: 'auto', width: 64 }} />
            ) : (
              value
            )}
          </Box>
        )}
      </Box>

      {menu && (
        <Menu
          open={menuPosition !== null}
          onClose={() => setMenuPosition(null)}
          anchorReference="anchorPosition"
          anchorPosition={menuPosition ?? undefined}
        >
          <MenuItem
            dense
            disabled={menu.disabled}
            data-testid={menu['data-testid']}
            onClick={() => {
              setMenuPosition(null);
              menu.onSelect();
            }}
          >
            {menu.label}
          </MenuItem>
        </Menu>
      )}
    </>
  );
};
